import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as financingApi from './api'
import type { ScheduleTemplateScope } from './api'
import type { AddSourceInput, AllocateInput, CashScheduleItem, Financing, RefundInput, RegisterPaymentInput, SaveCashScheduleTemplateInput, UpdateSourceInput } from './types'

export const financingKeys = {
  financing: (contractId: number) => ['financing', contractId] as const,
  banks: ['financing', 'banks'] as const,
  banksAdmin: ['banks', 'all'] as const,
}

function invalidateBanks(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: financingKeys.banks })
  qc.invalidateQueries({ queryKey: financingKeys.banksAdmin })
}

export function useFinancing(contractId: number) {
  return useQuery({
    queryKey: financingKeys.financing(contractId),
    queryFn: () => financingApi.getFinancing(contractId),
  })
}

export function useBanks() {
  return useQuery({
    queryKey: financingKeys.banks,
    queryFn: () => financingApi.listBanks(),
    staleTime: Infinity, // slownik - cache wiecznie
  })
}

// --- Slownik bankow (admin) ---

export function useBanksAdmin() {
  return useQuery({ queryKey: financingKeys.banksAdmin, queryFn: () => financingApi.listBanks(false) })
}

export function useCreateBank() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: { code: string; labelPl: string; sortOrder: number; isActive: boolean }) =>
      financingApi.createBank(payload),
    onSuccess: () => invalidateBanks(qc),
  })
}

export function useUpdateBank() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { code: string; payload: { labelPl: string; sortOrder: number; isActive: boolean } }) =>
      financingApi.updateBank(vars.code, vars.payload),
    onSuccess: () => invalidateBanks(qc),
  })
}

export function useDeleteBank() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (code: string) => financingApi.deleteBank(code),
    onSuccess: () => invalidateBanks(qc),
  })
}

export function useAddSource(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: AddSourceInput) => financingApi.addSource(contractId, input),
    onSuccess: (data: Financing) => {
      qc.setQueryData(financingKeys.financing(contractId), data)
      qc.invalidateQueries({ queryKey: gateKey(contractId) })
    },
  })
}

export function useUpdateSource(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { sourceId: number; input: UpdateSourceInput }) =>
      financingApi.updateSource(contractId, vars.sourceId, vars.input),
    onSuccess: (data: Financing) => {
      qc.setQueryData(financingKeys.financing(contractId), data)
      qc.invalidateQueries({ queryKey: gateKey(contractId) })
    },
  })
}

export function useDeleteSource(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (sourceId: number) => financingApi.deleteSource(contractId, sourceId),
    onSuccess: (data: Financing) => {
      qc.setQueryData(financingKeys.financing(contractId), data)
      qc.invalidateQueries({ queryKey: gateKey(contractId) })
    },
  })
}

// --- Harmonogram gotówki (Faza B) ---

const scheduleKey = (contractId: number, elementId: number) =>
  ['financing', 'schedule', contractId, elementId] as const

export function useCashSchedule(contractId: number, elementId: number) {
  return useQuery({
    queryKey: scheduleKey(contractId, elementId),
    queryFn: () => financingApi.listCashSchedule(contractId, elementId),
  })
}

export function useGenerateCashSchedule(contractId: number, elementId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (cashAmount: number) => financingApi.generateCashSchedule(contractId, elementId, cashAmount),
    onSuccess: (data: CashScheduleItem[]) => qc.setQueryData(scheduleKey(contractId, elementId), data),
  })
}

export function useConfirmTranche(contractId: number, elementId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { itemId: number; paidAt: string; paidAmount: number }) =>
      financingApi.confirmTranche(contractId, elementId, vars.itemId, { paidAt: vars.paidAt, paidAmount: vars.paidAmount }),
    onSuccess: () => qc.invalidateQueries({ queryKey: scheduleKey(contractId, elementId) }),
  })
}

// --- Harmonogram + wpłaty na poziomie umowy ---

const contractScheduleKey = (contractId: number) => ['financing', 'contract-schedule', contractId] as const
const paymentsKey = (contractId: number) => ['financing', 'payments', contractId] as const
// Bramki przejscia zaleza od sygnalow finansowych (harmonogram, statusy kredytow, wplaty),
// wiec po kazdej zmianie finansowania trzeba odswiezyc widok wymagan umowy.
const gateKey = (contractId: number) => ['gating', 'gate', contractId] as const

/**
 * Unieważnia wszystkie zapytania finansowania danej umowy. Wołać po zmianach, od których
 * finansowanie zależy a które dzieją się poza nim: ceny produktów (ELEMENT_PRICE) i kwota
 * umowy (total_amount). Inaczej zakładka pokazuje stare dane do wygaśnięcia staleTime.
 */
export function invalidateContractFinancing(qc: ReturnType<typeof useQueryClient>, contractId: number) {
  qc.invalidateQueries({ queryKey: financingKeys.financing(contractId) })
  qc.invalidateQueries({ queryKey: contractScheduleKey(contractId) })
  qc.invalidateQueries({ queryKey: ['financing', 'cash-split', contractId] })
  qc.invalidateQueries({ queryKey: paymentsKey(contractId) })
  qc.invalidateQueries({ queryKey: gateKey(contractId) })
}

export function useContractSchedule(contractId: number) {
  return useQuery({
    queryKey: contractScheduleKey(contractId),
    queryFn: () => financingApi.listContractSchedule(contractId),
  })
}

export function useCashSplit(contractId: number, cashAmount: number) {
  return useQuery({
    queryKey: ['financing', 'cash-split', contractId, cashAmount] as const,
    queryFn: () => financingApi.getCashSplit(contractId, cashAmount),
    enabled: cashAmount > 0,
  })
}

export function useContractPayments(contractId: number) {
  return useQuery({
    queryKey: paymentsKey(contractId),
    queryFn: () => financingApi.listPayments(contractId),
  })
}

export function useGenerateContractSchedule(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (cashAmount: number) => financingApi.generateContractSchedule(contractId, cashAmount),
    onSuccess: (data: CashScheduleItem[]) => {
      qc.setQueryData(contractScheduleKey(contractId), data)
      qc.invalidateQueries({ queryKey: financingKeys.financing(contractId) }) // cashRemaining się zmienia
      qc.invalidateQueries({ queryKey: gateKey(contractId) }) // harmonogram zmienia sygnaly bramki
    },
  })
}

export function useRegisterPayment(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: RegisterPaymentInput) => financingApi.registerPayment(contractId, input),
    onSuccess: () => invalidateContractFinancing(qc, contractId),
  })
}

export function useAllocatePayment(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { paymentId: number; input: AllocateInput }) =>
      financingApi.allocatePayment(contractId, vars.paymentId, vars.input),
    onSuccess: () => invalidateContractFinancing(qc, contractId),
  })
}

export function useDeleteAllocation(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { paymentId: number; allocationId: number }) =>
      financingApi.deleteAllocation(contractId, vars.paymentId, vars.allocationId),
    onSuccess: () => invalidateContractFinancing(qc, contractId),
  })
}

export function useRegisterRefund(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: RefundInput) => financingApi.registerRefund(contractId, input),
    onSuccess: () => invalidateContractFinancing(qc, contractId),
  })
}

export function useDeletePayment(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (paymentId: number) => financingApi.deletePayment(contractId, paymentId),
    onSuccess: () => invalidateContractFinancing(qc, contractId),
  })
}

// --- Szablony harmonogramu (admin) ---

const scheduleTemplateKey = (scope: ScheduleTemplateScope, code: string) =>
  ['financing', 'schedule-template', scope, code] as const

export function useScheduleTemplate(scope: ScheduleTemplateScope, code: string) {
  return useQuery({
    queryKey: scheduleTemplateKey(scope, code),
    queryFn: () => financingApi.getScheduleTemplate(scope, code),
    enabled: !!code,
  })
}

export function useSaveScheduleTemplate(scope: ScheduleTemplateScope, code: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: SaveCashScheduleTemplateInput) => financingApi.saveScheduleTemplate(scope, code, input),
    onSuccess: data => qc.setQueryData(scheduleTemplateKey(scope, code), data),
  })
}

export function useDeleteScheduleTemplate(scope: ScheduleTemplateScope, code: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => financingApi.deleteScheduleTemplate(scope, code),
    onSuccess: () => qc.invalidateQueries({ queryKey: scheduleTemplateKey(scope, code) }),
  })
}

